import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronRight, LogOut, Settings, Store } from 'lucide-react';
import { useRestaurant } from '../context/RestaurantContext';
import { useT } from '../context/LanguageContext';
import { locationsService } from '../services/locationsService';

/* HeaderUserChip — who is signed in, at the RIGHT end of the header as a bare
 * initials avatar. The name, the email and the role were spelled out on the
 * bar before; they are identity, not navigation, and reading them all day
 * costs a chunk of a row that the breadcrumb trail now uses. One tap opens
 * them, together with the things that are about YOU rather than about the
 * restaurant: preferences, which outlet you are looking at, and signing out.
 */
export default function HeaderUserChip({ user, onLogout, logoutLabel = 'Logout', footer }) {
  const [open, setOpen] = useState(false);
  const [outletsOpen, setOutletsOpen] = useState(false);
  const [outlets, setOutlets] = useState(null); // null = not fetched yet
  const ref = useRef(null);
  const navigate = useNavigate();
  const { t } = useT();
  const { restaurant, restaurantId, location, setLocationId } = useRestaurant();

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  // Closing the menu folds the outlet list back, so it reopens where it was left.
  useEffect(() => { if (!open) setOutletsOpen(false); }, [open]);

  /* The outlets are fetched only when somebody asks for them: most sessions
     never open this, and a branch list on every page load is a request the
     header does not need to make. */
  const loadOutlets = useCallback(() => {
    if (outlets !== null || !restaurantId) return;
    locationsService.list(restaurantId)
      .then((rows) => setOutlets((rows || []).filter((l) => l.is_active !== false)))
      .catch(() => setOutlets([])); // a failed list simply offers nothing
  }, [outlets, restaurantId]);

  if (!user) return null;
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ')
    || user.name || user.email || 'Signed in';
  const initials = name.split(/\s+/).map((w) => w[0]).filter(Boolean)
    .slice(0, 2).join('').toUpperCase() || '·';
  // "Owner · The Indian Symphony" — the role means little without the place it is a role AT.
  const roleLine = [user.role, restaurant?.name].filter(Boolean).join(' · ');

  const go = (to) => { setOpen(false); navigate(to); };
  const ITEM = 'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm '
    + 'text-foreground transition-colors hover:bg-accent';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        /* The name is the accessible label: sighted users get the initials,
           a screen reader gets the person. */
        aria-label={name}
        title={name}
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold
          transition-colors ${open
    ? 'bg-primary text-primary-foreground'
    : 'bg-primary/15 text-primary hover:bg-primary/25'}`}
      >
        {initials}
      </button>

      {open && (
        /* Anchored to the RIGHT edge: the trigger is the last thing on the bar,
           so a left-anchored menu would hang off the window. */
        <div role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-lg border border-border bg-card shadow-lg">
          <div className="flex items-start gap-2.5 border-b border-border px-3 py-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
              {initials}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-foreground">{name}</span>
              {user.email && (
                <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
              )}
            </span>
          </div>

          {roleLine && (
            <div className="px-3 pt-2.5">
              <span className="inline-flex max-w-full items-center rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                <span className="truncate">{roleLine}</span>
              </span>
            </div>
          )}

          <div className="py-1.5">
            <button type="button" role="menuitem" className={ITEM}
              onClick={() => go('/settings/preferences')}>
              <Settings size={15} className="text-muted-foreground" />
              {t('userMenu.preferences')}
            </button>

            {/* Which branch every screen is reading. Only offered when there
                is more than one — a single-outlet restaurant has no choice to
                make, and a menu row that never does anything is noise. */}
            <button type="button" role="menuitem"
              className={`${ITEM} justify-between`}
              aria-expanded={outletsOpen}
              onClick={() => { loadOutlets(); setOutletsOpen((v) => !v); }}>
              <span className="flex min-w-0 items-center gap-2.5">
                <Store size={15} className="shrink-0 text-muted-foreground" />
                <span className="truncate">{t('userMenu.switchOutlet')}</span>
              </span>
              <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                <span className="max-w-[70px] truncate">{location?.name || ''}</span>
                <ChevronRight size={13}
                  className={`transition-transform ${outletsOpen ? 'rotate-90' : ''}`} />
              </span>
            </button>

            {outletsOpen && (
              <div className="max-h-52 overflow-y-auto border-y border-border bg-muted/30">
                {outlets === null ? (
                  <div className="px-3 py-2 text-xs text-muted-foreground">
                    {t('common.loading')}
                  </div>
                ) : outlets.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-muted-foreground">
                    {t('userMenu.noOutlets')}
                  </div>
                ) : outlets.map((o) => {
                  const here = o.id === location?.id;
                  return (
                    <button key={o.id} type="button" role="menuitem"
                      onClick={() => { setLocationId(o.id); setOpen(false); }}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-2 pl-10 text-left text-sm
                        transition-colors hover:bg-accent ${here ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                      <span className="truncate">{o.name}</span>
                      {here && <Check size={14} className="shrink-0 text-primary" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onLogout && onLogout(); }}
            className="flex w-full items-center gap-2.5 border-t border-border px-3 py-2.5 text-left text-sm text-destructive transition-colors hover:bg-destructive/10"
          >
            <LogOut size={15} /> {logoutLabel}
          </button>

          {/* Which build is running — reference material, not something to
              read all day, so it sits at the bottom of the menu. */}
          {footer && (
            <div className="border-t border-border bg-muted/40 px-3 py-1.5">{footer}</div>
          )}
        </div>
      )}
    </div>
  );
}
